import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ClampText from './ClampText';
import Modal from './Modal';
import TravelMap, { TravelMapModal } from './TravelMap';
import VehicleIcon from './VehicleIcons';
import useInView from '../hooks/useInView';
import '../css/ProjectCard.css';

function resolveImagePath(imagePath) {
  if (typeof imagePath !== 'string' || imagePath.length === 0) {
    return '';
  }

  const normalizedPath = imagePath.replace(/^\/+/, '');
  return `${process.env.PUBLIC_URL}/${normalizedPath}`;
}

// A journey rendered as a first-class project card (Traveler Mode).
function TravelCard({ trip, language, order, activeTag = '', onTagClick = undefined }) {
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showMapModal, setShowMapModal] = useState(false);
  const [cardRef, isVisible] = useInView({ threshold: 0.08 });

  const title = language === 'ja' ? trip.title_ja : trip.title_en;
  const period = language === 'ja' ? trip.period_ja : trip.period_en;
  const paragraphs = (language === 'ja' ? trip.description_ja : trip.description_en) || [];
  const tags = trip.tags || [];
  const images = trip.images || [];
  const resolvedImages = images.map((image) => resolveImagePath(image.src));
  const imageAlts = images.map((image) => (language === 'ja' ? image.alt_ja : image.alt_en));
  const hasRoute = Boolean(trip.route);
  const isTagFilterable = typeof onTagClick === 'function';

  const swiperA11y =
    language === 'ja'
      ? {
          prevSlideMessage: '前のスライド',
          nextSlideMessage: '次のスライド',
          containerMessage: '写真スライダー',
        }
      : undefined;

  const copy =
    language === 'ja'
      ? {
          index: 'JOURNEY',
          map: '行程を追体験する',
          album: '写真をもっと見る',
          pending: '記録整理中',
          expand: '拡大',
        }
      : {
          index: 'JOURNEY',
          map: 'Replay the route',
          album: 'More photos',
          pending: 'LOG PENDING',
          expand: 'Expand',
        };

  return (
    <article
      id={`travel-${trip.id}`}
      ref={cardRef}
      className={`project-card project-card--compact project-card--travel reveal ${
        isVisible ? 'is-visible' : ''
      }`}
    >
      <div className="project-card__media">
        {images.length > 0 ? (
          <Swiper
            modules={[A11y, Navigation, Pagination]}
            navigation
            pagination={{ type: 'fraction' }}
            a11y={swiperA11y}
            loop={images.length > 1}
            spaceBetween={14}
            className="project-swiper"
          >
            {images.map((image, imageIndex) => (
              <SwiperSlide key={`${trip.id}-image-${imageIndex}`} className="project-slide">
                <img
                  src={resolvedImages[imageIndex]}
                  alt={language === 'ja' ? image.alt_ja : image.alt_en}
                  className="project-slide__image project-slide__image--travel"
                  loading="lazy"
                />
                <button
                  type="button"
                  onClick={() => {
                    setSelectedImageIndex(imageIndex);
                    setShowPhotoModal(true);
                  }}
                  className="project-slide__expand mono"
                >
                  {copy.expand}
                </button>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : hasRoute ? (
          <TravelMap
            routeId={trip.route}
            routeKind={trip.routeKind}
            cities={trip.cities}
            vehicle={trip.vehicle}
            language={language}
            variant="thumb"
          />
        ) : (
          <div className="project-card__media-empty">
            <VehicleIcon type={trip.vehicle} strokeWidth={1.2} />
          </div>
        )}
      </div>

      {showPhotoModal ? (
        <Modal
          images={resolvedImages}
          alts={imageAlts}
          currentIndex={selectedImageIndex}
          onSelectImage={setSelectedImageIndex}
          onClose={() => setShowPhotoModal(false)}
          title={title}
          language={language}
        />
      ) : null}

      {showMapModal ? (
        <TravelMapModal project={trip} language={language} onClose={() => setShowMapModal(false)} />
      ) : null}

      <div className="project-card__body">
        <p className="project-card__index mono project-card__index--travel">
          <VehicleIcon type={trip.vehicle} className="project-card__vehicle" strokeWidth={1.8} />
          {copy.index} {String(order).padStart(2, '0')}
          {trip.placeholder ? <span className="project-card__pending"> · {copy.pending}</span> : null}
        </p>
        <h4 className="project-card__title">{title}</h4>
        <p className="project-card__period mono">{period}</p>

        <div className="project-card__tags">
          {tags.map((tag) => {
            const label = language === 'ja' ? tag.ja : tag.en;
            const isActiveTag = activeTag === tag.ja;
            const tagClasses = [
              'tag',
              `tag--travel-${tag.cat}`,
              isTagFilterable ? 'tag--clickable' : '',
              isActiveTag ? 'is-active' : '',
            ]
              .filter(Boolean)
              .join(' ');

            if (!isTagFilterable) {
              return (
                <span key={`${trip.id}-${tag.ja}`} className={tagClasses}>
                  {label}
                </span>
              );
            }

            return (
              <button
                key={`${trip.id}-${tag.ja}`}
                type="button"
                className={tagClasses}
                onClick={() => onTagClick(tag.ja)}
                aria-pressed={isActiveTag}
              >
                {label}
              </button>
            );
          })}
        </div>

        <ClampText language={language}>
          <div className="project-card__story">
            {paragraphs.map((paragraph, index) => (
              <p key={`${trip.id}-para-${index}`} className="project-card__description">
                {paragraph}
              </p>
            ))}
          </div>
        </ClampText>

        {hasRoute || trip.albumUrl ? (
          <div className="project-card__actions">
            {hasRoute ? (
              <button type="button" className="button button--map" onClick={() => setShowMapModal(true)}>
                <VehicleIcon type={trip.vehicle} className="button__glyph" strokeWidth={1.8} />
                {copy.map}
              </button>
            ) : null}
            {trip.albumUrl ? (
              <a
                href={trip.albumUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="button button--ghost button--album"
              >
                {copy.album} ↗
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}

export default TravelCard;
